// Bluetooth Mesh lamps controlled REMOTELY through the location's hub — an
// ESP32BLEGATEWAY (DeviceType 103) on Wi-Fi + BLE Mesh. docs/20 is the contract.
//
// Same interface as MeshSession's control half (onOff, ctl, lightness,
// temperature, readState, + blink), so useBleNode picks ONE of them per
// command and the rest of the code does not care which: phone Bluetooth when
// the lamps are in range, this when they are not (or on a phone with Bluetooth
// off, or no radio at all).
//
// Every command waits for the hub's OWN reply — MESHOK / MESHERR / MESHSTATE,
// matched by command name and our user id — because publishing to a broker
// that is up but a hub that is not is otherwise a silent success (seen on the
// bench: commands in the ~25 s after a hub restart vanished).
//
// ⚠️ PAIRING, CONFIGURING AND RESETTING STAY ON THE PHONE. The hub only
// controls; it never holds a lamp's device key.

import {
  addMessageListener, publishMessage, subscribeTopic, unsubscribeTopic, waitForConnect,
} from '../../mqtt/mqttClient';
import { noteHub } from './hubStatus';
import { getDeviceDetail, getDevicesByLocation, getDevicesByUser } from '../../api/api';
import { elementWith, MODEL } from './meshSession';

export const HUB_DEVICE_ID = 103;
const HUB_TYPE_NAME = /BLEGATEWAY/i;
const REPLY_TIMEOUT_MS = 6000;

const hex4 = (n) => Number(n).toString(16).toUpperCase().padStart(4, '0');

/** "[A0A3…]MESHOK:MESHONOFF#1" -> { uno, body: "MESHOK:MESHONOFF", tag: "1" } or null. */
export function parseHubReply(payload) {
  const s = String(payload || '');
  const open = s.indexOf('[');
  const close = s.indexOf(']');
  const hash = s.lastIndexOf('#');
  if (open !== 0 || close < 1 || hash < close) return null;
  return { uno: s.slice(1, close), body: s.slice(close + 1, hash), tag: s.slice(hash + 1) };
}

/** "MESHSTATE:0012,1,65535,2700" -> { addr: 0x12, on, lightness, kelvin } or null. */
export function parseMeshState(body) {
  const m = /^MESHSTATE:([0-9A-F]{4}),([01]),(\d+),(\d+)$/i.exec(String(body || ''));
  if (!m) return null;
  return {
    addr: parseInt(m[1], 16), on: m[2] === '1', lightness: Number(m[3]), kelvin: Number(m[4]) || null,
  };
}

const ERR_WORDS = {
  NOKEYS: 'The hub has not been given this location\'s mesh keys yet.',
  NOREPLY: 'The lamp did not answer the hub — is it powered?',
  BUSY: 'The hub is busy — try again in a moment.',
  BADARGS: 'The hub did not understand the command.',
  NOTSUPPORTED: 'The hub\'s firmware does not support this yet.',
  // Pairing / configuring through the hub (firmware 1.1.0+)
  NOTFOUND: 'The hub has not seen that lamp waiting to be paired — factory-reset it, then scan again.',
  TIMEOUT: 'The lamp stopped answering while pairing — move the hub closer, or try again.',
  FAILED: 'The lamp refused to pair — factory-reset it and try again.',
  NONODE: 'The hub could not keep this lamp in its table.',
};

/** "MESHERR:MESHONOFF,NOREPLY" -> a sentence. */
export function hubErrorText(body) {
  const code = String(body || '').split(',').pop();
  return ERR_WORDS[code] || `The hub refused: ${body}`;
}

// ── which hub serves a location ─────────────────────────────────────────────
const hubCache = new Map(); // locationId -> { uno|null, at }
const hubPending = new Map(); // locationId -> the lookup in flight
const HUB_CACHE_MS = 5 * 60 * 1000;

/** The server already said which hub serves a location (GetBleNodeByUniqueNumber `hub`). */
export function rememberHub(locationId, uno) {
  const id = Number(locationId);
  if (id) hubCache.set(id, { uno: uno ? String(uno) : null, at: Date.now() });
}

/**
 * The hub at a location, or null. The location's device list names the type
 * (DeviceTypeName "ESP32BLEGATEWAY") but carries no DeviceID, so a candidate is
 * confirmed from its own row. Cached for a few minutes, a "none" included.
 */
export async function findHub(locationId, userId) {
  const id = Number(locationId);
  if (!id) return null;
  const hit = hubCache.get(id);
  if (hit && Date.now() - hit.at < HUB_CACHE_MS) return hit.uno;
  // ⚠️ ONE LOOKUP PER LOCATION, SHARED. The fallback reads the location's whole
  // device list (~8 s for one location); four BLE cards each starting their own
  // left all four spinning. Every caller now waits on the same promise.
  if (hubPending.has(id)) return hubPending.get(id);
  const p = lookupHub(id, userId).finally(() => hubPending.delete(id));
  hubPending.set(id, p);
  return p;
}

/** The first candidate whose own device row says it is a hub AT this location. */
async function confirmHub(candidates, id) {
  for (const c of candidates) {
    // eslint-disable-next-line no-await-in-loop
    const d = await getDeviceDetail(c.UniqueNumber).catch(() => null);
    const row = Array.isArray(d?.data) ? d.data[0] : d?.data;
    if (row && Number(row.DeviceID) === HUB_DEVICE_ID && Number(row.LocationID) === id) return String(c.UniqueNumber);
  }
  return null;
}

const hubRows = (res) => (Array.isArray(res?.data) ? res.data : [])
  .filter((r) => Number(r.DeviceID) === HUB_DEVICE_ID || HUB_TYPE_NAME.test(String(r.DeviceTypeName || r.DeviceName || '')));

async function lookupHub(id, userId) {
  let uno = null;
  try {
    // ⚠️ FAST PATH FIRST. The user's own device list answers in ~0.1 s (and is
    // local-first on a phone); the location list took ~8 s, and the card sat on
    // "Finding this location's hub…" with the hub online the whole time. The
    // location list is kept only for a Super Admin browsing a location they are
    // not mapped to.
    if (userId) {
      const mine = await getDevicesByUser(userId).catch(() => null);
      uno = await confirmHub(hubRows(mine), id);
    }
    if (!uno) uno = await confirmHub(hubRows(await getDevicesByLocation(id)), id);
  } catch (e) {
    uno = null;
  }
  hubCache.set(id, { uno, at: Date.now() });
  return uno;
}

export class HubTransport {
  constructor(uno, userId) {
    this.isHub = true;
    this.uno = String(uno);
    this.userId = String(userId || '');
  }

  /**
   * Publish `[uno]cmd#userId`, resolve with the hub's reply body for that command.
   * `expect` = the reply prefix that ends it, for commands that answer with
   * data (MESHCOMP:, MESHCFG:, MESHPROVED:, MESHRANGE:); `onLine(body)` sees
   * every other reply on the way (MESHSCAN's MESHUNPROV: lines).
   */
  async send(cmd, { timeoutMs = REPLY_TIMEOUT_MS, expect = null, onLine = null } = {}) {
    const name = cmd.split(':')[0];
    const topic = `${this.uno}/client`;
    if (!(await waitForConnect(6000))) throw new Error('No internet connection to reach the hub.');
    subscribeTopic(topic);
    try {
      const reply = await new Promise((resolve) => {
        const timer = setTimeout(() => { off(); resolve(null); }, timeoutMs);
        const off = addMessageListener((t, payload) => {
          if (t !== topic) return;
          const r = parseHubReply(String(payload));
          if (!r || r.uno !== this.uno || (r.tag && r.tag !== this.userId)) return;
          const mine = r.body === `MESHOK:${name}` || r.body.startsWith(`MESHERR:${name}`)
            || (expect && r.body.startsWith(expect))
            || (name === 'MESHGET' && r.body.startsWith('MESHSTATE:'))
            || (name === 'MESHPING' && r.body.startsWith('MESHNET:'));
          if (!mine) {
            if (onLine) onLine(r.body);
            return;
          }
          clearTimeout(timer);
          off();
          resolve(r.body);
        });
        publishMessage(`${this.uno}/device`, `[${this.uno}]${cmd}#${this.userId}`);
      });
      // Any reply (even MESHERR) proves the hub is live; silence proves it is not.
      noteHub(this.uno, reply != null);
      if (reply == null) throw new Error('The hub did not answer — is it online?');
      if (reply.startsWith('MESHERR:')) throw new Error(hubErrorText(reply));
      return reply;
    } finally {
      unsubscribeTopic(topic);
    }
  }

  // eslint-disable-next-line class-methods-use-this
  async ensureProxy() {}

  async onOff(dst, on) {
    await this.send(`MESHONOFF:${hex4(dst)},${on ? 1 : 0}`);
    return null;
  }

  async ctl(dst, lightness, kelvin) {
    await this.send(`MESHCTL:${hex4(dst)},${Math.round(lightness)},${Math.round(kelvin)}`);
    return null;
  }

  async lightness(dst, lightness) {
    await this.send(`MESHLEVEL:${hex4(dst)},${Math.round(lightness)}`);
  }

  async temperature(dst, kelvin) {
    await this.send(`MESHTEMP:${hex4(dst)},${Math.round(kelvin)}`);
  }

  /** The hub runs the whole blink itself — one command, steady rhythm over the internet. */
  async blink(dst, count, offMs, onMs, endOn) {
    await this.send(`MESHBLINK:${hex4(dst)},${count},${offMs},${onMs},${endOn ? 1 : 0}`, {
      timeoutMs: count * (offMs + onMs) + 6000,
    });
  }

  /** -> { on, lightness, kelvin } as the lamp reports it (via the hub). */
  async readState(lamp) {
    const onAddr = elementWith(lamp, MODEL.ONOFF) ?? Number(lamp.MeshAddress);
    const ctlAddr = elementWith(lamp, MODEL.CTL) ?? Number(lamp.MeshAddress);
    const body = await this.send(onAddr === ctlAddr ? `MESHGET:${hex4(onAddr)}` : `MESHGET:${hex4(onAddr)},${hex4(ctlAddr)}`);
    const st = parseMeshState(body);
    if (!st) throw new Error('The hub sent no state');
    return { on: st.on, lightness: st.lightness, kelvin: st.kelvin };
  }
}
